import { TriangleAlert } from 'lucide-react'
import { Suspense, use, useState } from 'react'
import { QueryRefsContext } from '@/components/root/query-refs-context'
import { ManageBillingButton } from '@/components/shared/manage-billing-button'
import { P } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useReadQuery } from '@/lib/hooks'
import { isExpiredCard } from '@/lib/utils'

const ExpiredPaymentMethodNoticeContent = () => {
  const { companyBillingQueryRef } = use(QueryRefsContext)
  const { data } = useReadQuery(companyBillingQueryRef)
  const [isOpen, setOpen] = useState(false)

  const defaultPaymentMethod = data?.company?.defaultPaymentMethod
  const isExpired = Boolean(
    defaultPaymentMethod?.id &&
    isExpiredCard({
      expYear: defaultPaymentMethod.expYear,
      expMonth: defaultPaymentMethod.expMonth,
    })
  )

  if (!isExpired) {
    return null
  }

  const expiryLabel =
    defaultPaymentMethod?.expMonth && defaultPaymentMethod?.expYear
      ? `${defaultPaymentMethod.expMonth}/${defaultPaymentMethod.expYear}`
      : null

  const onClose = () => {
    setOpen(false)
  }

  return (
    <>
      <Button variant="destructive" size="sm" onClick={() => setOpen(true)}>
        <TriangleAlert aria-hidden="true" />
        <span>Payment method expired</span>
      </Button>
      <Separator orientation="vertical" className="h-4!" />
      <Sheet open={isOpen} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <SheetContent
          className="sm:max-w-[500px] overflow-hidden"
          onEscapeKeyDown={() => onClose()}
        >
          <SheetHeader className="pb-0">
            <SheetTitle className="text-destructive">
              Expired payment method
            </SheetTitle>
          </SheetHeader>
          <div className="px-4">
            <P className="mt-0">
              Your default payment method has expired
              {expiryLabel ? ` (${expiryLabel})` : ''}.
            </P>
            <P>
              <b>
                Update your default payment method to avoid failed charges and
                service interruptions.
              </b>
            </P>
            <P>Manage your payment methods through our billing portal.</P>
          </div>
          <SheetFooter className="sticky">
            <ManageBillingButton variant="destructive" />
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  )
}

export const ExpiredPaymentMethodNotice = () => {
  return (
    <Suspense fallback={null}>
      <ExpiredPaymentMethodNoticeContent />
    </Suspense>
  )
}
